import moment from 'moment';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useGeneralProjectionSettings, useProjection } from '../../api';
import { PAYLOAD_DATE_FORM } from '../../config/constants';
import { SAEngineProjection } from '../../enums';
import type { ExtendedSeriesOptionType, IMonteCarloProjectionSeries, ISerieEntry } from '../../interfaces/projection';

const CHART_TYPE = 'spline';
const LINE_WITH = 3;

export const useMonteCarloSeries = (clientOutput?: boolean, projectionPeriodInYears?: number): ExtendedSeriesOptionType[] => {
    const { t } = useTranslation();

    const { data: generalSimulationSettings } = useGeneralProjectionSettings();
    const isDefaultScenario = generalSimulationSettings?.scenario === 'DEFAULT';

    const { data: projection, isSuccess } = useProjection(projectionPeriodInYears);

    const createSeriesArray = useCallback((serieEntries: ISerieEntry[], payloadDateForm: string) => {
        const series: Array<string | number>[] = [[]];
        for (let i = 0; i < serieEntries?.length; i++) {
            series[i] = [];
            for (let j = 0; j < 2; j++) {
                if (j === 0) {
                    series[i][j] = moment(serieEntries[i].date, payloadDateForm).valueOf();
                } else {
                    series[i][j] = serieEntries[i].amount;
                }
            }
        }

        return series;
    }, []);

    const getLatestAmount = useCallback((data: Array<string | number>[]): number => {
        const lastEntry = data[data.length - 1];
        if (lastEntry?.length === 2) {
            return data[data.length - 1][1] as number;
        } else {
            return 0;
        }
    }, []);

    return useMemo(() => {
        if (!isSuccess) {
            return [];
        }
        const projectionSeries: IMonteCarloProjectionSeries = {
            expected: [],
            median: [],
            percentileOptimistic: [],
            percentileConservative: [],
            custom: [],
        };

        const { twr, mwr } = projection.returns!;

        projection?.projections?.forEach((monteCarloSeries) => {
            if (monteCarloSeries.path === SAEngineProjection.EXPECTED) {
                projectionSeries.expected = createSeriesArray(monteCarloSeries.timeseries, PAYLOAD_DATE_FORM);
            } else if (monteCarloSeries.path === SAEngineProjection.MEDIAN) {
                projectionSeries.median = createSeriesArray(monteCarloSeries.timeseries, PAYLOAD_DATE_FORM);
            } else if (monteCarloSeries.path === SAEngineProjection.OPTIMISTIC) {
                projectionSeries.percentileOptimistic = createSeriesArray(monteCarloSeries.timeseries, PAYLOAD_DATE_FORM);
            } else if (monteCarloSeries.path === SAEngineProjection.CONSERVATIVE) {
                projectionSeries.percentileConservative = createSeriesArray(monteCarloSeries.timeseries, PAYLOAD_DATE_FORM);
            } else if (monteCarloSeries.path === SAEngineProjection.CUSTOM) {
                projectionSeries.custom = createSeriesArray(monteCarloSeries.timeseries, PAYLOAD_DATE_FORM);
            }
        });

        const series: ExtendedSeriesOptionType[] = [
            {
                twr: twr[SAEngineProjection.OPTIMISTIC],
                mwr: mwr[SAEngineProjection.OPTIMISTIC],
                showInNavigator: true,
                showInLegend: true,
                customShowRectSymbol: !!clientOutput,
                name: t('optimistic'),
                path: SAEngineProjection.OPTIMISTIC,
                amountAtProjectEnd: getLatestAmount(projectionSeries.percentileOptimistic),
                color: '#B0AA7E',
                type: CHART_TYPE,
                cursor: 'pointer',
                lineWidth: LINE_WITH,
                data: projectionSeries.percentileOptimistic,
                marker: {
                    symbol: 'circle',
                },
            },
            {
                twr: twr[SAEngineProjection.MEDIAN],
                mwr: mwr[SAEngineProjection.MEDIAN],
                showInNavigator: true,
                showInLegend: true,
                customShowRectSymbol: !!clientOutput,
                name: t(SAEngineProjection.MEDIAN),
                path: SAEngineProjection.MEDIAN,
                amountAtProjectEnd: getLatestAmount(projectionSeries.median),
                type: CHART_TYPE,
                cursor: 'pointer',
                color: '#141e55',
                lineWidth: LINE_WITH,
                data: projectionSeries.median,
                marker: {
                    symbol: 'circle',
                },
            },
            {
                twr: twr[SAEngineProjection.CONSERVATIVE],
                mwr: mwr[SAEngineProjection.CONSERVATIVE],
                showInNavigator: true,
                showInLegend: true,
                customShowRectSymbol: !!clientOutput,
                name: t('conservative'),
                path: SAEngineProjection.CONSERVATIVE,
                amountAtProjectEnd: getLatestAmount(projectionSeries.percentileConservative),
                color: '#818288',
                type: CHART_TYPE,
                cursor: 'pointer',
                lineWidth: LINE_WITH,
                data: projectionSeries.percentileConservative,
                marker: {
                    symbol: 'circle',
                },
            },
            {
                twr: twr[SAEngineProjection.EXPECTED],
                mwr: mwr[SAEngineProjection.EXPECTED],
                showInNavigator: true,
                showInLegend: true,
                customShowRectSymbol: !!clientOutput,
                name: t('expected'),
                path: SAEngineProjection.EXPECTED,
                amountAtProjectEnd: getLatestAmount(projectionSeries.expected),
                color: '#008675',
                type: CHART_TYPE,
                cursor: 'pointer',
                lineWidth: LINE_WITH,
                data: projectionSeries.expected,
                marker: {
                    symbol: 'circle',
                },
            },
            {
                twr: twr[SAEngineProjection.CUSTOM],
                mwr: mwr[SAEngineProjection.CUSTOM],
                showInNavigator: true,
                showInLegend: true,
                customShowRectSymbol: !!clientOutput,
                name: t('custom'),
                path: SAEngineProjection.CUSTOM,
                amountAtProjectEnd: getLatestAmount(projectionSeries.custom),
                color: '#845960ff',
                type: CHART_TYPE,
                cursor: 'pointer',
                lineWidth: LINE_WITH,
                data: projectionSeries.custom,
                marker: {
                    symbol: 'circle',
                },
            },
        ];

        return (series ?? []).filter((s) =>
            isDefaultScenario ? ['optimistic', 'conservative', 'median'].includes(s.path) : ['expected', 'custom'].includes(s.path),
        );
    }, [isDefaultScenario, projection, clientOutput, t, createSeriesArray, getLatestAmount, isSuccess]);
};
